const MY_HEALTH_MODULES = ['op', 'ma', 'dn'];
const MY_HEALTH_MODULE_LABELS = { op: 'OP', ma: 'MA', dn: 'DN' };

// Mirrors TOBController::MY_HEALTH_PLAN_PREFIXES / MY_HEALTH_TIER_TOKENS on the backend. Each
// module (op/ma/dn) can now be pinned to its own tier, independent of the base (HS) tier, e.g.
// "my_health_indonesia_essential_dn_elite" pulls Dental from the Elite column while the rest of
// the plan stays Essential.
const MY_HEALTH_COUNTRY_PREFIXES = ['my_health_singapore', 'my_health_indonesia', 'my_health_philippines'];
const MY_HEALTH_TIER_TOKENS = ['core', 'essential', 'extensive', 'elite'];

// MyHEALTH Philippines has no plain "Essential" HS plan — only the "_100k"/"_500k" annual-maximum
// variants, which both resolve to the same underlying "essential" tier/column.
const MY_HEALTH_PHILIPPINES_ESSENTIAL_VARIANTS = ['100k', '500k'];

// Mirrors TOBController::stripMyHealthPhilippinesEssentialVariant: strips a Philippines
// "_100k"/"_500k" variant segment so the remainder can be resolved/parsed like any other MyHEALTH
// base id.
function stripMyHealthPhilippinesEssentialVariant(planId) {
    for (const variant of MY_HEALTH_PHILIPPINES_ESSENTIAL_VARIANTS) {
        const prefix = `my_health_philippines_essential_${variant}`;

        if (planId === prefix || planId.startsWith(`${prefix}_`)) {
            return `my_health_philippines_essential${planId.slice(prefix.length)}`;
        }
    }

    return planId;
}

// Per-country HS (base) tier options and the tier tokens each op/ma/dn module can be
// independently pinned to. Drives AddTobDialog's MyHEALTH per-component selector (a select per
// component, rather than a flat list of every module x tier combination) and buildMyHealthCompositePlan below.
export const MY_HEALTH_INSURERS_CONFIG = {
    my_health_singapore: {
        label: 'April (MyHealth) Singapore',
        hsOptions: [
            { id: 'my_health_singapore_essential', label: 'Essential' },
            { id: 'my_health_singapore_extensive', label: 'Extensive' },
            { id: 'my_health_singapore_elite', label: 'Elite' },
        ],
        moduleTierOptions: [
            { value: 'essential', label: 'Essential' },
            { value: 'extensive', label: 'Extensive' },
            { value: 'elite', label: 'Elite' },
        ],
    },
    my_health_indonesia: {
        label: 'April (MyHealth) Indonesia',
        hsOptions: [
            { id: 'my_health_indonesia_core', label: 'Core' },
            { id: 'my_health_indonesia_essential', label: 'Essential' },
            { id: 'my_health_indonesia_extensive', label: 'Extensive' },
            { id: 'my_health_indonesia_elite', label: 'Elite' },
        ],
        moduleTierOptions: [
            { value: 'core', label: 'Core' },
            { value: 'essential', label: 'Essential' },
            { value: 'extensive', label: 'Extensive' },
            { value: 'elite', label: 'Elite' },
        ],
    },
    my_health_philippines: {
        label: 'April (MyHealth) Philippines',
        hsOptions: [
            { id: 'my_health_philippines_essential_100k', label: 'Essential (100k)' },
            { id: 'my_health_philippines_essential_500k', label: 'Essential (500k)' },
            { id: 'my_health_philippines_extensive', label: 'Extensive' },
            { id: 'my_health_philippines_elite', label: 'Elite' },
        ],
        moduleTierOptions: [
            { value: 'essential', label: 'Essential' },
            { value: 'extensive', label: 'Extensive' },
            { value: 'elite', label: 'Elite' },
        ],
    },
};

// Builds a MyHEALTH composite plan id/label from a chosen HS (base) tier plus, for each op/ma/dn
// module, either null ("N/A" — not covered) or a pinned tier value from that module's
// moduleTierOptions. Mirrors the suffix format TOBController::parseMyHealthCompositePlanId
// expects and MyHealth*_Compact_Rates_Dialog builds: modules in their fixed op/ma/dn order, each
// immediately followed by its own tier token. Returns null if insurerKey/hsId aren't recognized.
export function buildMyHealthCompositePlan(insurerKey, hsId, moduleTiers) {
    const config = MY_HEALTH_INSURERS_CONFIG[insurerKey];
    const hsOption = config?.hsOptions.find((option) => option.id === hsId);

    if (!hsOption) return null;

    const idParts = [];
    const labelParts = [];

    for (const moduleKey of MY_HEALTH_MODULES) {
        const tierValue = moduleTiers[moduleKey];

        if (!tierValue) continue;

        idParts.push(`${moduleKey}_${tierValue}`);
        const tierOption = config.moduleTierOptions.find((option) => option.value === tierValue);
        labelParts.push(`${MY_HEALTH_MODULE_LABELS[moduleKey]} ${tierOption?.label || tierValue}`);
    }

    const id = idParts.length ? `${hsId}_${idParts.join('_')}` : hsId;
    const label = labelParts.length ? `${hsOption.label} + ${labelParts.join(' + ')}` : hsOption.label;

    return { id, label };
}

// Mirrors TOBController::OPTIMUM_GLOBAL_MODULE_ROWS / OPTIMUM_GLOBAL_TIERS_WITHOUT_MATERNITY on
// the backend, and the suffix built by OptimumGlobal_Compact_Rates_Dialog's
// `[...selectedAddOns].sort().join('_')`.
const OPTIMUM_GLOBAL_BASE_PLAN_IDS = [
    'optimum_global_emerald',
    'optimum_global_sapphire',
    'optimum_global_ruby',
    'optimum_global_jade',
    'optimum_global_diamond',
];
const OPTIMUM_GLOBAL_MODULES = ['dental', 'maternity', 'optical'];
const OPTIMUM_GLOBAL_MODULE_LABELS = { dental: 'Dental', maternity: 'Maternity', optical: 'Optical' };
const OPTIMUM_GLOBAL_TIERS_WITHOUT_MATERNITY = ['optimum_global_emerald', 'optimum_global_sapphire'];

// All 6 optional covers a real Morgan Price quote can carry (MorganPrice_Compact_Rates_Dialog.js
// OPTIONAL_COVERS, and TOBController::MORGAN_PRICE_MODULE_TOKENS on the backend), in the same
// canonical order the dialog always emits them in.
const MORGAN_PRICE_BASE_PLAN_IDS = ['morgan_price_core_ip', 'morgan_price_core_op1', 'morgan_price_core_op2'];
const MORGAN_PRICE_MODULE_TOKENS = [
    'remove_op_co_insurance',
    'reduce_overall_annual_limit',
    'dental',
    'maternity_module',
    'enhanced_network_module',
    'increase_si',
];

// Mirrors TOBController::VUMI_BASE_PLAN_IDS / VUMI_ADD_ON_TOKENS on the backend, and the suffix
// built by Vumi_Compact_Rates_Dialog's ADD_ON_ID_KEYS-ordered join (extended_evacuation,
// non_emergency_evacuation, wellness_and_optical_500/1000, outpatient_treatment).
const VUMI_BASE_PLAN_IDS = ['vumi_basic', 'vumi_standard', 'vumi_superior', 'vumi_ultra', 'vumi_total'];
const VUMI_BASIC_PLAN_ID = 'vumi_basic';
const VUMI_ADD_ON_TOKENS = [
    'extended_evacuation',
    'non_emergency_evacuation',
    'wellness_and_optical_500',
    'wellness_and_optical_1000',
    'outpatient_treatment',
];

// remove_op_co_insurance and enhanced_network_module don't change any TOB row/value today (see
// TOBController::MORGAN_PRICE_MODULE_ROWS/ANNUAL_MAX_OVERRIDES) so every combo involving them
// would render identically to the one without — they're left out of the curated picker list
// below to avoid a 192-entry (2^6 x 3 bases) dropdown full of visual duplicates. Real quoted
// plan_ids that do include them are still recognized by isValidMorganPriceTobId, just not
// individually listed here.
const MORGAN_PRICE_ANNUAL_LIMIT_VARIANTS = [
    null,
    { key: 'reduce_overall_annual_limit', label: 'Reduced Annual Limit' },
    { key: 'increase_si', label: 'Increased SI' },
];
const MORGAN_PRICE_TOGGLE_MODULES = [
    { key: 'dental', label: 'Dental' },
    { key: 'maternity_module', label: 'Maternity' },
];

// Generates the curated Morgan Price picker list for a base plan: every combination of the
// Dental/Maternity toggles crossed with the (mutually exclusive) standard/reduced/increased
// annual limit choice. Suffix order always follows MORGAN_PRICE_MODULE_TOKENS' canonical order
// (annual limit token before Dental/Maternity for "reduce", after for "increase") so these ids
// match what MorganPrice_Compact_Rates_Dialog itself would produce for the same selection.
function buildMorganPriceTierPlans(baseId, baseLabel) {
    const plans = [];

    for (const annualLimit of MORGAN_PRICE_ANNUAL_LIMIT_VARIANTS) {
        for (let mask = 0; mask < (1 << MORGAN_PRICE_TOGGLE_MODULES.length); mask++) {
            const modules = MORGAN_PRICE_TOGGLE_MODULES.filter((_, i) => mask & (1 << i));
            const parts = [];

            if (annualLimit?.key === 'reduce_overall_annual_limit') parts.push(annualLimit);
            parts.push(...modules);
            if (annualLimit?.key === 'increase_si') parts.push(annualLimit);

            const id = [baseId, ...parts.map((p) => p.key)].join('_');
            const label = parts.length ? `${baseLabel} + ${parts.map((p) => p.label).join(' + ')}` : baseLabel;

            plans.push({ id, label });
        }
    }

    return plans;
}

// Structural check mirroring TOBController::parseMorganPriceCompositePlanId on the backend:
// true for a Morgan Price base plan optionally followed by any combination of the 6 known
// module tokens, in any order. Lets addCorrespondingTob recognize real quoted Morgan Price plan
// ids (which can carry any of the 6 optional covers, not just the curated subset enumerated in
// TOB_INSURERS above) without needing every combination pre-listed there.
export function isValidMorganPriceTobId(planId) {
    const basePlanId = MORGAN_PRICE_BASE_PLAN_IDS.find((id) => planId === id || planId.startsWith(`${id}_`));

    if (!basePlanId) return false;

    let remaining = planId.slice(basePlanId.length).replace(/^_/, '');
    const seenTokens = new Set();

    while (remaining !== '') {
        const token = MORGAN_PRICE_MODULE_TOKENS.find((t) => remaining === t || remaining.startsWith(`${t}_`));

        if (!token || seenTokens.has(token)) return false;

        seenTokens.add(token);
        remaining = remaining.slice(token.length).replace(/^_/, '');
    }

    return true;
}

// Structural check mirroring TOBController::parseMyHealthCompositePlanId on the backend: true for
// a MyHEALTH base (HS) tier optionally followed by any combination of op/ma/dn modules, each
// immediately followed by its own pinned tier token (in any order, e.g.
// "..._essential_op_essential_dn_elite"). Lets addCorrespondingTob recognize real quoted MyHEALTH
// plan ids carrying per-module tiers, which aren't individually enumerated in TOB_INSURERS above
// (that would be a combinatorial explosion of module x tier combinations).
export function isValidMyHealthTobId(planId) {
    const lookupId = stripMyHealthPhilippinesEssentialVariant(planId);
    const countryPrefix = MY_HEALTH_COUNTRY_PREFIXES.find((prefix) => lookupId.startsWith(`${prefix}_`));

    if (!countryPrefix) return false;

    const remainingAfterCountry = lookupId.slice(countryPrefix.length + 1);
    const baseTier = MY_HEALTH_TIER_TOKENS.find((tier) => remainingAfterCountry === tier || remainingAfterCountry.startsWith(`${tier}_`));

    if (!baseTier) return false;

    let remaining = remainingAfterCountry.slice(baseTier.length).replace(/^_/, '');
    const seenModules = new Set();

    while (remaining !== '') {
        const module = MY_HEALTH_MODULES.find((m) => remaining.startsWith(`${m}_`));

        if (!module || seenModules.has(module)) return false;

        seenModules.add(module);
        remaining = remaining.slice(module.length + 1);

        const tier = MY_HEALTH_TIER_TOKENS.find((t) => remaining === t || remaining.startsWith(`${t}_`));

        if (!tier) return false;

        remaining = remaining.slice(tier.length).replace(/^_/, '');
    }

    return true;
}

// Generates every add-on combination for an Optimum Global tier (base tier plus each non-empty
// subset of its applicable modules). Maternity isn't offered on Emerald/Sapphire at all, so it's
// left out of their module list entirely — the id suffix must match what
// OptimumGlobal_Compact_Rates_Dialog builds via [...selectedAddOns].sort().join('_').
function buildOptimumGlobalTierPlans(baseId, baseLabel) {
    const modules = OPTIMUM_GLOBAL_TIERS_WITHOUT_MATERNITY.includes(baseId)
        ? OPTIMUM_GLOBAL_MODULES.filter((m) => m !== 'maternity')
        : OPTIMUM_GLOBAL_MODULES;

    const plans = [{ id: baseId, label: baseLabel }];

    for (let mask = 1; mask < (1 << modules.length); mask++) {
        const selected = modules.filter((_, i) => mask & (1 << i));
        const idSuffix = [...selected].sort().join('_');
        const label = `${baseLabel} + ${selected.map((m) => OPTIMUM_GLOBAL_MODULE_LABELS[m]).join(' + ')}`;
        plans.push({ id: `${baseId}_${idSuffix}`, label });
    }

    return plans;
}

// Structural check mirroring TOBController::parseOptimumGlobalCompositePlanId on the backend:
// true for an Optimum Global base tier optionally followed by any combination of the 3 known
// add-on tokens, in any order. Lets addCorrespondingTob recognize real quoted Optimum Global plan
// ids, including a "maternity" add-on selected for Emerald/Sapphire (the backend treats it as a
// no-op for those two tiers rather than rejecting it), without needing every combination
// pre-listed in TOB_INSURERS above.
export function isValidOptimumGlobalTobId(planId) {
    const basePlanId = OPTIMUM_GLOBAL_BASE_PLAN_IDS.find((id) => planId === id || planId.startsWith(`${id}_`));

    if (!basePlanId) return false;

    const remaining = planId.slice(basePlanId.length).replace(/^_/, '');

    if (remaining === '') return true;

    const modules = remaining.split('_');
    const seenModules = new Set();

    for (const module of modules) {
        if (!OPTIMUM_GLOBAL_MODULES.includes(module) || seenModules.has(module)) return false;
        seenModules.add(module);
    }

    return true;
}

// Structural check mirroring TOBController::parseVumiCompositePlanId on the backend: true for a
// VUMI base tier optionally followed by any combination of the 5 known add-on tokens, in any
// order, excluding wellness_and_optical_500 and wellness_and_optical_1000 together (mutually
// exclusive on the backend). Lets addCorrespondingTob recognize real quoted VUMI plan ids
// carrying add-ons without needing every combination pre-listed in TOB_INSURERS above.
export function isValidVumiTobId(planId) {
    const basePlanId = VUMI_BASE_PLAN_IDS.find((id) => planId === id || planId.startsWith(`${id}_`));

    if (!basePlanId) return false;

    let remaining = planId.slice(basePlanId.length).replace(/^_/, '');

    if (remaining === '') return true;

    const seenTokens = new Set();

    while (remaining !== '') {
        const token = VUMI_ADD_ON_TOKENS.find((t) => remaining === t || remaining.startsWith(`${t}_`));

        if (!token || seenTokens.has(token)) return false;

        seenTokens.add(token);
        remaining = remaining.slice(token.length).replace(/^_/, '');
    }

    if (seenTokens.has('wellness_and_optical_500') && seenTokens.has('wellness_and_optical_1000')) return false;

    return true;
}

// Generates the curated VUMI picker list for a tier: the bare tier plus its two
// wellness_and_optical variants. Wellness and Optical isn't offered on Basic (see
// TOBController::VUMI_BASIC_PLAN_ID), so its variants are left out there. The other 3 add-on
// tokens (extended_evacuation, non_emergency_evacuation, outpatient_treatment) don't change any
// TOB row/value (see TOBController::VUMI_WELLNESS_AND_OPTICAL_VALUE_OVERRIDES), so — like Morgan
// Price's remove_op_co_insurance/enhanced_network_module — they're left out of this curated list
// to avoid visual duplicates; real quoted plan_ids that include them are still recognized by
// isValidVumiTobId, just not individually listed here.
function buildVumiTierPlans(baseId, baseLabel) {
    if (baseId === VUMI_BASIC_PLAN_ID) {
        return [{ id: baseId, label: baseLabel }];
    }

    return [
        { id: baseId, label: baseLabel },
        { id: `${baseId}_wellness_and_optical_500`, label: `${baseLabel} + Wellness and Optical ($500)` },
        { id: `${baseId}_wellness_and_optical_1000`, label: `${baseLabel} + Wellness and Optical ($1000)` },
    ];
}

export const TOB_INSURERS = [
    {
        key: 'cigna',
        label: 'Cigna',
        plans: [
            { id: 'cigna_silver', label: 'Silver' },
            { id: 'cigna_gold', label: 'Gold' },
            { id: 'cigna_platinum', label: 'Platinum' },
        ],
    },
    {
        key: 'lifeline',
        label: 'BUPA Lifeline',
        plans: [
            { id: 'lifeline_essential', label: 'Essential' },
            { id: 'lifeline_classic', label: 'Classic' },
            { id: 'lifeline_gold', label: 'Gold' },
        ],
    },
    {
        key: 'bwho',
        label: 'BUPA WHO',
        // Modular: WMI is the required base module, WMP/WME/WW are independently combinable
        plans: [
            { id: 'bwho_wmi', label: 'WMI' },
            { id: 'bwho_wmi_wmp', label: 'WMI + WMP' },
            { id: 'bwho_wmi_wme', label: 'WMI + WME' },
            { id: 'bwho_wmi_ww', label: 'WMI + WW' },
            { id: 'bwho_wmi_wmp_wme', label: 'WMI + WMP + WME' },
            { id: 'bwho_wmi_wmp_ww', label: 'WMI + WMP + WW' },
            { id: 'bwho_wmi_wme_ww', label: 'WMI + WME + WW' },
            { id: 'bwho_wmi_wmp_wme_ww', label: 'WMI + WMP + WME + WW' },
        ],
    },
    {
        key: 'ihhp',
        label: 'BUPA IHHP KSK',
        plans: [
            { id: 'ihhp_hospital_plan', label: 'Hospital Plan' },
            { id: 'ihhp_non_hospitalisation_benefits', label: 'Module 1' },
            { id: 'ihhp_medicine_appliances', label: 'Module 2' },
            { id: 'ihhp_dental_optical_a', label: 'Module 4A' },
            { id: 'ihhp_dental_optical_b', label: 'Module 4B' },
        ],
    },
    {
        key: 'vumi',
        label: 'VUMI',
        plans: [
            ...buildVumiTierPlans('vumi_basic', 'Basic'),
            ...buildVumiTierPlans('vumi_standard', 'Standard'),
            ...buildVumiTierPlans('vumi_superior', 'Superior'),
            ...buildVumiTierPlans('vumi_ultra', 'Ultra'),
            ...buildVumiTierPlans('vumi_total', 'Total'),
        ],
    },
    {
        key: 'optimum_global',
        label: 'AXA - Optimum Global',
        plans: [
            ...buildOptimumGlobalTierPlans('optimum_global_emerald', 'Emerald'),
            ...buildOptimumGlobalTierPlans('optimum_global_sapphire', 'Sapphire'),
            ...buildOptimumGlobalTierPlans('optimum_global_ruby', 'Ruby'),
            ...buildOptimumGlobalTierPlans('optimum_global_jade', 'Jade'),
            ...buildOptimumGlobalTierPlans('optimum_global_diamond', 'Diamond'),
        ],
    },
    {
        key: 'morgan_price',
        label: 'Morgan Price International',
        plans: [
            ...buildMorganPriceTierPlans('morgan_price_core_ip', 'Core'),
            ...buildMorganPriceTierPlans('morgan_price_core_op1', 'Core + OP1'),
            ...buildMorganPriceTierPlans('morgan_price_core_op2', 'Core + OP2'),
        ],
    },
    {
        key: 'morgan_price_evolution',
        label: 'Morgan Price - Evolution Health Plan',
        plans: [
            { id: 'morgan_price_evolution_standard', label: 'Standard' },
            { id: 'morgan_price_evolution_standard_plus', label: 'Standard Plus' },
            { id: 'morgan_price_evolution_comprehensive', label: 'Comprehensive' },
            { id: 'morgan_price_evolution_premium', label: 'Premium' },
            { id: 'morgan_price_evolution_elite', label: 'Elite' },
        ],
    },
    {
        key: 'allianz_care',
        label: 'Allianz Care',
        plans: [
            { id: 'allianz_care_care', label: 'Care IP + Active OP + Smile Dental' },
            { id: 'allianz_care_care_plus', label: 'Care Plus IP + Active Plus OP + Bloom Maternity + Smile Dental' },
            { id: 'allianz_care_care_pro', label: 'Care Pro IP + Active Pro OP + Bloom Plus Maternity + Smile Plus Dental' },
        ],
    },
    {
        key: 'allianz_dublin',
        label: 'Allianz Dublin',
        plans: [
            { id: 'allianz_dublin_care_base', label: 'Care Base' },
            { id: 'allianz_dublin_care_enhanced', label: 'Care Enhanced' },
            { id: 'allianz_dublin_care_signature', label: 'Care Signature' },
        ],
    },
    {
        key: 'allianz_care_singapore',
        label: 'Allianz Singapore',
        plans: [
            { id: 'allianz_care_singapore_essential', label: 'Essential IP + Crystal OP + Dental 3' },
            { id: 'allianz_care_singapore_classic', label: 'Classic IP + Bronze OP + Dental 3' },
            { id: 'allianz_care_singapore_club', label: 'Club IP + Silver OP + Bloom Maternity + Dental 2' },
            { id: 'allianz_care_singapore_premier', label: 'Premier IP + Gold OP + Bloom Plus Maternity + Dental 1' },
        ],
    },
    {
        key: 'img_global_prima',
        label: 'IMG Global Prima',
        plans: [
            { id: 'img_global_prima_bronze', label: 'Bronze' },
            { id: 'img_global_prima_bronze_plus', label: 'Bronze Plus' },
            { id: 'img_global_prima_silver', label: 'Silver' },
            { id: 'img_global_prima_gold', label: 'Gold' },
            { id: 'img_global_prima_platinum', label: 'Platinum' },
        ],
    },
    {
        key: 'pacific_cross_select',
        label: 'Pacific Cross Select',
        plans: [
            { id: 'pacific_cross_select_plus_ward', label: 'Select Plus (Ward)' },
            { id: 'pacific_cross_select_plus_semi_private', label: 'Select Plus (Semi-Private)' },
            { id: 'pacific_cross_select_plus_private_2m', label: 'Select Plus (Private 2M)' },
            { id: 'pacific_cross_select_plus_private_3m', label: 'Select Plus (Private 3M)' },
            { id: 'pacific_cross_select_plus_private_5m', label: 'Select Plus (Private 5M)' },
            { id: 'pacific_cross_select_standard_ward', label: 'Select Standard (Ward)' },
            { id: 'pacific_cross_select_standard_semi_private', label: 'Select Standard (Semi-Private)' },
            { id: 'pacific_cross_select_standard_private_2m', label: 'Select Standard (Private 2M)' },
        ],
    },
    {
        key: 'pacific_cross_primary_care_plan',
        label: 'Pacific Cross PrimaryCare',
        plans: [
            { id: 'pacific_cross_primary_care_plan_core', label: 'Core' },
            { id: 'pacific_cross_primary_care_plan_pulse', label: 'Pulse' },
            { id: 'pacific_cross_primary_care_plan_complete', label: 'Complete' },
        ],
    },
    {
        key: 'pacific_cross_blue_royale',
        label: 'Pacific Cross Blue Royale',
        plans: [
            { id: 'pacific_cross_blue_royale_plan_a', label: 'Plan A' },
            { id: 'pacific_cross_blue_royale_plan_b', label: 'Plan B' },
            { id: 'pacific_cross_blue_royale_plan_c', label: 'Plan C' },
        ],
    },
    // MyHEALTH plans are picked via AddTobDialog's dedicated per-component selector (see
    // MY_HEALTH_INSURERS_CONFIG/buildMyHealthCompositePlan above) rather than this flat plans
    // list, since each op/ma/dn module can be pinned to its own tier — enumerating every
    // combination here would be a combinatorial explosion. The bare HS tiers are still listed so
    // TOB_PLAN_IDS (used elsewhere for quick lookups) includes them; isValidMyHealthTobId covers
    // validating any module/tier combination.
    {
        key: 'my_health_indonesia',
        label: MY_HEALTH_INSURERS_CONFIG.my_health_indonesia.label,
        plans: MY_HEALTH_INSURERS_CONFIG.my_health_indonesia.hsOptions.map(({ id, label }) => ({ id, label })),
    },
    {
        key: 'my_health_singapore',
        label: MY_HEALTH_INSURERS_CONFIG.my_health_singapore.label,
        plans: MY_HEALTH_INSURERS_CONFIG.my_health_singapore.hsOptions.map(({ id, label }) => ({ id, label })),
    },
    {
        key: 'my_health_philippines',
        label: MY_HEALTH_INSURERS_CONFIG.my_health_philippines.label,
        plans: MY_HEALTH_INSURERS_CONFIG.my_health_philippines.hsOptions.map(({ id, label }) => ({ id, label })),
    },
];
